import { Button } from '@mantine/core'
import type { DoseSpotLaunchFailure } from '@/pages/Patient/components/doseSpotLaunchFailure'

type Props = {
  failure: DoseSpotLaunchFailure
  /** Re-mints. Absent actions are simply not offered; `failure.retryable` still gates it. */
  onRetry: () => void
  /** Opens the patient editor. Only the patient-scoped launch has a fix path. */
  onFix?: () => void
  onClose: () => void
}

/**
 * Every way a DoseSpot launch can refuse, rendered once.
 *
 * Shared by the two launch surfaces — the patient-scoped Prescribe modal and the bell's
 * patient-less notification modal — because **the refusal states are identical between them**.
 * There is one mint-only endpoint and no eligibility query, so a blocked provider discovers it
 * by clicking on whichever surface they happened to click; they must not then read two
 * different accounts of the same refusal.
 *
 * It lives in `components/` rather than beside `describeDoseSpotLaunchFailure` (which decodes
 * the error this renders) because only this half is shared: the decoder is imported by the
 * chart's modal and by this file, and moving it would churn its tests for no gain.
 *
 * The **server's message is preferred over local copy** — it carries the field list, the
 * administrator path and DoseSpot's own account wording — so what is authored here is the
 * layout and which actions each state offers.
 */
export const DoseSpotLaunchFailurePanel = ({ failure, onRetry, onFix, onClose }: Props) => (
  <div className="mx-auto max-w-xl py-16 text-center">
    <div className="text-base font-semibold">{failure.title}</div>
    <p className="mt-2 text-sm text-gray-600">{failure.message}</p>
    {failure.detail && (
      <p className="mt-1 text-xs text-gray-500">DoseSpot says: {failure.detail}</p>
    )}
    {failure.fields.length > 0 && (
      <ul className="mt-3 inline-block list-disc text-left text-sm text-gray-700">
        {failure.fields.map((field, index) => (
          <li key={field}>
            {field}
            {/*
             * DoseSpot's own reason for this field, when it sent one. The two lists are the
             * keys and values of one `ModelState` object, so they share an order by
             * construction.
             */}
            {failure.messages[index] && (
              <span className="text-gray-500"> — {failure.messages[index]}</span>
            )}
          </li>
        ))}
      </ul>
    )}
    <div className="mt-5 flex justify-center gap-2">
      {onFix && failure.fixLabel && <Button onClick={onFix}>{failure.fixLabel}</Button>}
      {failure.retryable && (
        // Re-mints. A retry that re-navigated to the same URL would always fail — the code is
        // consumed.
        <Button variant={onFix && failure.fixLabel ? 'default' : 'filled'} onClick={onRetry}>
          Try again
        </Button>
      )}
      <Button variant="subtle" onClick={onClose}>
        Close
      </Button>
    </div>
  </div>
)
